import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import Progress from "@/components/Progress";
import Select from "@/components/Select";
import SegmentedButton from "@/components/SegmentedButton";
import Switch from "@/components/Switch";
import Tag from "@/components/Tag";
import TextField from "@/components/TextField";
import { useNavigate, type Path } from "@/router";
import {
  ACTIVE_CALL_SESSION_CHANGED_EVENT,
  clearActiveCallSelectionForCallSid,
  markActiveCallEndedForCallSid,
  readActiveCallSelection,
  type ActiveCallSelection
} from "@/layouts/AppLayout/IncomingCallEventIntegration/sessionStorage";
import {
  loadStagedTranscript,
  loadTranscriptHistory,
  loadTranscriptSettings,
  pushTranscriptHistory,
  removeTranscriptHistoryEntry,
  restoreSeedTranscripts,
  saveStagedTranscript,
  saveTranscriptSettings,
  SEED_TRANSCRIPTS,
  transcriptSnippet,
  transcriptTitleForText,
  type EmulatorTranscriptEntry,
  type EmulatorTranscriptSettings
} from "./transcripts";
import TranscriptPicker from "./TranscriptPicker";
import useEmulatorRequestOptions from "./useEmulatorRequestOptions";
import useFlexEmulatorSession from "./useFlexEmulatorSession";
import { incomingCallPathFor, setFlexEmulatorSessionActive } from "./session";
import {
  completeEmulatorCall,
  EMULATOR_BACKEND_NOT_ENABLED_MESSAGE,
  EMULATOR_TRANSCRIPT_OUTCOME_OPTIONS,
  fetchEmulatorCall,
  fetchRecentCalls,
  fetchSampleCaller,
  FlexEmulatorError,
  isEmulatorNotEnabledError,
  deliverEmulatorTranscript,
  startEmulatorCall,
  type EmulatorCall,
  type EmulatorDirection
} from "./api";

// Twilio brand red — intentionally not a sys-brand token.
const TWILIO_RED = "#F22F46";
// White label + icon for the header Reset Button on the red bar (same token-
// override pattern as PersonObjectHeadingRefresh).
const HEADER_BUTTON_STYLE = {
  "--md-text-button-label-text-color": "white",
  "--md-text-button-hover-label-text-color": "white",
  "--md-text-button-focus-label-text-color": "white",
  "--md-text-button-pressed-label-text-color": "white",
  "--md-text-button-icon-color": "white",
  "--md-text-button-hover-icon-color": "white",
  "--md-text-button-focus-icon-color": "white",
  "--md-text-button-pressed-icon-color": "white"
} as CSSProperties;
// Above the side-sheet stack (drawer portal: scrim 1000, sheet 1001, tab 1002)
// so dock clicks never hit the dismiss scrim while a flyout is open.
const DOCK_Z_INDEX_CLASS = "z-[2000]";

const DIRECTION_OPTIONS = [
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound-api" }
];

// Twilio reports withheld caller ID as a raw From string, not E.164:
// "anonymous" is canonical, "unknown" appears from some carriers.
const CALLER_ID_OPTIONS = [
  { label: "Phone number", value: "number" },
  { label: "Anonymous (withheld)", value: "anonymous" },
  { label: "Unknown (unavailable)", value: "unknown" }
];

type CallerIdKind = "number" | "anonymous" | "unknown";

const DEBUG_LOG_KEY = "m360.flexEmulatorDebugLog";
const MAX_DEBUG_ENTRIES = 30;

type DebugEntry = { time: string; message: string };

// Route changes remount the dock (Start navigates to the ringing URL), so the
// action record lives in sessionStorage: per-tab, survives reloads, gone with
// the tab.
const loadDebugLog = (): DebugEntry[] => {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(DEBUG_LOG_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is DebugEntry =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as DebugEntry).time === "string" &&
        typeof (entry as DebugEntry).message === "string"
    );
  } catch {
    return [];
  }
};

const PHASE_STEPS = ["Call", "Talk", "Transcript"] as const;

type DockBusyKey = "load" | "end" | "transcript" | "start" | "sample" | "recent";

type DockPhase = "idle" | "loading" | "live" | "wrapup" | "done";

type DeliveredSnapshot = { outcome: string; text: string | null; landsAt: number | null };

type DeliveredSnapshots = Record<string, DeliveredSnapshot>;

const DELIVERED_SNAPSHOTS_KEY = "m360.flexEmulatorDelivered";

// The delivered-state rides out dock remounts in sessionStorage, like the
// debug log: agent-flow navigations remount the dock, and a remount must not
// resurrect the delivery form and offer a second delivery for the call. One
// entry per call SID so a round-trip through recent calls hydrates each
// delivered call instead of clearing it.
const loadDeliveredSnapshots = (): DeliveredSnapshots => {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(DELIVERED_SNAPSHOTS_KEY) ?? "{}");
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    return parsed as DeliveredSnapshots;
  } catch {
    return {};
  }
};

const saveDeliveredSnapshot = (callSid: string, snapshot: DeliveredSnapshot): void => {
  try {
    const snapshots = loadDeliveredSnapshots();
    snapshots[callSid] = snapshot;
    window.sessionStorage.setItem(DELIVERED_SNAPSHOTS_KEY, JSON.stringify(snapshots));
  } catch {
    // Storage unavailable; the snapshot lasts for this mount only.
  }
};

const clearDeliveredSnapshot = (callSid: string): void => {
  try {
    const snapshots = loadDeliveredSnapshots();
    if (!(callSid in snapshots)) return;
    delete snapshots[callSid];
    window.sessionStorage.setItem(DELIVERED_SNAPSHOTS_KEY, JSON.stringify(snapshots));
  } catch {
    // Storage unavailable; nothing to clear.
  }
};

const describeError = (err: unknown): string => {
  if (isEmulatorNotEnabledError(err)) return EMULATOR_BACKEND_NOT_ENABLED_MESSAGE;
  if (err instanceof FlexEmulatorError) {
    if (err.status === 401) return "Your session expired. Please log in again.";
    return err.message;
  }
  return "Something went wrong. Please try again.";
};

const interactionPathFor = (pgaId: string, interactionId: string): Path =>
  `/people/${encodeURIComponent(pgaId)}/interactions/interactions/${interactionId}` as Path;

const formatCallSid = (callSid: string) =>
  callSid.length <= 17 ? callSid : `${callSid.slice(0, 13)}…${callSid.slice(-4)}`;

const directionLabelFor = (direction: string | null): string => (direction === "outbound-api" ? "Outbound" : "Inbound");

const callerLabelFor = (phone: string | null | undefined): string => {
  if (phone === "anonymous") return "Anonymous caller";
  if (phone === "unknown") return "Unknown caller";
  return phone ?? "Unknown caller";
};

const phaseStepIndexFor = (phase: DockPhase): number => {
  switch (phase) {
    case "idle":
      return 0;
    case "loading":
    case "live":
      return 1;
    case "wrapup":
    case "done":
      return 2;
  }
};

function RecentStatusTag({ call }: { call: EmulatorCall }) {
  if (!call.endedAt) {
    return (
      <Tag tagType="success" tagStyle="standard" size="small">
        Live
      </Tag>
    );
  }
  if (call.interactionId) {
    return (
      <Tag tagType="information" tagStyle="standard" size="small">
        Finalized
      </Tag>
    );
  }
  return (
    <Tag tagType="surface" tagStyle="standard" size="small">
      Ended
    </Tag>
  );
}

function PhaseSteps({ phase }: { phase: DockPhase }) {
  const activeIndex = phaseStepIndexFor(phase);
  return (
    <div
      role="group"
      className="flex items-center gap-1 border-b border-sys-brand-outline-variant px-4 py-2"
      aria-label={`Emulator progress: step ${activeIndex + 1} of ${PHASE_STEPS.length}`}
    >
      {PHASE_STEPS.map((label, index) => (
        <div key={label} className="flex min-w-0 flex-1 items-center gap-1">
          {index > 0 && (
            <div
              aria-hidden="true"
              className={`h-px min-w-2 flex-1 ${index <= activeIndex ? "bg-sys-brand-primary" : "bg-sys-brand-outline-variant"}`}
            />
          )}
          <div className="flex items-center gap-1">
            <span
              aria-hidden="true"
              className={`flex h-5 w-5 items-center justify-center rounded-full text-label-small ${
                index < activeIndex
                  ? "bg-sys-brand-primary text-sys-brand-on-primary"
                  : index === activeIndex
                    ? "bg-sys-brand-primary-container text-sys-brand-on-primary-container"
                    : "bg-sys-brand-surface-container-high text-sys-brand-on-surface-variant"
              }`}
            >
              {index < activeIndex ? <Icon name="check" size={12} /> : index + 1}
            </span>
            <span
              className={`truncate text-label-small ${
                index === activeIndex ? "font-bold text-sys-brand-on-surface" : "text-sys-brand-on-surface-variant"
              }`}
            >
              {label}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The whole emulator in one floating panel (DEV builds, emulator session
 * only): start and end calls, deliver transcripts, curate the transcript
 * library, and browse recent calls — without leaving the product UI. Keeps the
 * last call around after the agent flow clears its selection so late transcripts
 * can still be delivered post-finalize.
 *
 * Deliberately decoupled from the app's downstream systems: the dock never
 * displays AI summaries and never drives the wrap-up flow. Twilio's only
 * contribution is the call transcription, so that is all the dock deals in —
 * the backend enforces its own delivery rules and surfaces them as errors.
 *
 * Transcript selection only appears when it matters: compact "on hangup"
 * staging during a live call with auto-delivery on, the full picker after
 * hangup, and a collapsed library disclosure when idle. Once a delivery
 * lands, the form collapses to a compact delivered-state — delivery is
 * terminal per call, so only a fresh call or Reset restores the form. The
 * picker always sits inside a default-collapsed accordion and renders
 * full-height — never a scrolling window inside the accordion, so the dock
 * keeps one scroll region: its own.
 */
export default function FlexEmulatorDock() {
  const session = useFlexEmulatorSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const requestOptions = useEmulatorRequestOptions();
  const [selection, setSelection] = useState<ActiveCallSelection | null>(null);
  const [lastCallSid, setLastCallSid] = useState<string | null>(null);
  const [lastPgaId, setLastPgaId] = useState<string | null>(null);
  const [call, setCall] = useState<EmulatorCall | null>(null);
  const [phone, setPhone] = useState("");
  const [callerIdKind, setCallerIdKind] = useState<CallerIdKind>("number");
  const [direction, setDirection] = useState<EmulatorDirection>("inbound");
  const [initialStaged] = useState(loadStagedTranscript);
  const [transcriptOutcome, setTranscriptOutcome] = useState(initialStaged.outcome);
  const [transcriptText, setTranscriptText] = useState(initialStaged.text);
  const [history, setHistory] = useState<EmulatorTranscriptEntry[]>(() => loadTranscriptHistory());
  const [initialSettings] = useState(loadTranscriptSettings);
  const [settings, setSettings] = useState(initialSettings);
  const [delaySeconds, setDelaySeconds] = useState(() => String(initialSettings.delaySeconds || ""));
  const [recentCalls, setRecentCalls] = useState<EmulatorCall[]>([]);
  const [initialDebugLog] = useState(loadDebugLog);
  const [debugLog, setDebugLog] = useState(initialDebugLog);
  const [debugOpen, setDebugOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(true);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [hangupPickerOpen, setHangupPickerOpen] = useState(false);
  const [lateTranscriptOpen, setLateTranscriptOpen] = useState(false);
  const [transcriptAccordionOpen, setTranscriptAccordionOpen] = useState(false);
  // Snapshot of the delivery for this call: once set, wrapup/late show a
  // compact delivered-state instead of the full form. Delivery is terminal
  // per call — real Twilio serves one immutable transcript per recording —
  // so only a reset clears it and restores the form. Snapshots persist per
  // call SID in sessionStorage so remounts and call-switch round-trips
  // hydrate them back instead of resurrecting the form.
  const [lastDelivered, setLastDelivered] = useState<DeliveredSnapshot | null>(null);
  // Clock for the scheduled-delivery countdown; ticks only while a landing is pending.
  const [now, setNow] = useState(() => Date.now());
  // In-flight action keys (a set, not a single enum) so overlapping loads
  // can't clear each other's busy state early.
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<DockBusyKey>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const startBusy = useCallback((key: DockBusyKey) => {
    setBusyKeys((current) => (current.has(key) ? current : new Set(current).add(key)));
  }, []);

  const endBusy = useCallback((key: DockBusyKey) => {
    setBusyKeys((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }, []);

  // What the emulator did, newest last, for the debug panel. Opens the panel
  // on first entry so the record is visible without hunting for it. Persists
  // synchronously: Start logs and navigates in the same tick, which remounts
  // the dock and would discard a state update that never flushed. The write is
  // idempotent (overwrite, not append), so updater re-invocation is harmless.
  const logDebug = useCallback((message: string) => {
    const time = new Date().toLocaleTimeString([], { hour12: false });
    setDebugLog((current) => {
      const next = [...current.slice(-(MAX_DEBUG_ENTRIES - 1)), { time, message }];
      try {
        window.sessionStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(next));
      } catch {
        // Private-mode quota or similar: the in-memory log still renders.
      }
      return next;
    });
    setDebugOpen(true);
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(debugLog));
    } catch {
      // Private-mode quota or similar: the in-memory log still renders.
    }
  }, [debugLog]);

  useEffect(() => {
    if (!session) return;

    const refreshSelection = () => setSelection(readActiveCallSelection());
    refreshSelection();
    window.addEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
    window.addEventListener("storage", refreshSelection);

    return () => {
      window.removeEventListener(ACTIVE_CALL_SESSION_CHANGED_EVENT, refreshSelection);
      window.removeEventListener("storage", refreshSelection);
    };
  }, [session]);

  useEffect(() => {
    if (selection?.callSid) {
      setLastCallSid(selection.callSid);
    }
    if (selection?.pgaId) {
      setLastPgaId(selection.pgaId);
    }
  }, [selection?.callSid, selection?.pgaId]);

  const loadRecentCalls = useCallback(async () => {
    startBusy("recent");
    setError(null);
    try {
      setRecentCalls(await fetchRecentCalls({ ...requestOptions, limit: 5 }));
    } catch (err) {
      setError(describeError(err));
    } finally {
      endBusy("recent");
    }
  }, [requestOptions, startBusy, endBusy]);

  const loadCall = useCallback(
    async (callSid: string) => {
      startBusy("load");
      setError(null);
      try {
        setCall(await fetchEmulatorCall(callSid, requestOptions));
      } catch (err) {
        setCall(null);
        setError(describeError(err));
      } finally {
        endBusy("load");
      }
    },
    [requestOptions, startBusy, endBusy]
  );

  const activeCallSid = selection?.callSid ?? lastCallSid;
  const hasActiveCall = activeCallSid !== null && activeCallSid !== undefined;

  // Recent calls are the idle home; collapse them once a call takes focus so
  // the transcript picker owns the visible panel.
  useEffect(() => {
    setRecentOpen(!hasActiveCall);
  }, [hasActiveCall]);

  // Per-call disclosures reset when switching calls. The delivered snapshot
  // hydrates back for the same call (remounts must not resurrect the form);
  // only a call switch clears it. A null CallSid is transitional (mount
  // before the selection syncs), so it clears state but keeps storage for
  // the hydrate that follows.
  useEffect(() => {
    setHangupPickerOpen(false);
    setLateTranscriptOpen(false);
    setTranscriptAccordionOpen(false);
    if (!activeCallSid) {
      setLastDelivered(null);
      return;
    }
    const stored = loadDeliveredSnapshots()[activeCallSid] ?? null;
    setLastDelivered(stored);
  }, [activeCallSid]);

  useEffect(() => {
    if (!session) return;
    void loadRecentCalls();
  }, [session, loadRecentCalls]);

  // Reload when the CallSid changes or the agent flow clears its selection
  // (finalize) so the dock reveals the finalized state. Selection mutations
  // that keep the same CallSid (e.g. the end-flip) skip the reload because the
  // action that caused them already holds fresh state.
  const loadedCallSidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!session || !activeCallSid) {
      setCall(null);
      loadedCallSidRef.current = null;
      return;
    }
    if (selection && loadedCallSidRef.current === activeCallSid) return;
    loadedCallSidRef.current = activeCallSid;
    void loadCall(activeCallSid);
  }, [session, selection, activeCallSid, loadCall]);

  // Ticks the scheduled-delivery countdown and flips it to delivered-state
  // when the landing time passes. landsAt-based (not seconds-based) so a
  // remount resumes the countdown instead of restarting it. Already-landed
  // snapshots flip immediately so a late remount never flashes a stale count.
  useEffect(() => {
    if (lastDelivered?.landsAt == null || !activeCallSid) return;
    const landsAt = lastDelivered.landsAt;
    const snapshot = lastDelivered;
    const callSid = activeCallSid;
    const land = () => {
      const landed = { ...snapshot, landsAt: null };
      setLastDelivered(landed);
      saveDeliveredSnapshot(callSid, landed);
    };
    if (Date.now() >= landsAt) {
      land();
      return;
    }
    const timer = window.setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= landsAt) land();
    }, 1000);
    return () => window.clearInterval(timer);
  }, [lastDelivered, activeCallSid]);

  const isEnded = !!call?.endedAt;

  if (!session) return null;

  const updateSettings = (patch: Partial<EmulatorTranscriptSettings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch };
      saveTranscriptSettings(next);
      return next;
    });
  };

  const selectTranscript = (text: string) => {
    setTranscriptText(text);
    setLastDelivered(null);
    saveStagedTranscript({ text, outcome: transcriptOutcome });
  };

  const busyAny = busyKeys.size > 0;
  const stagedTranscript = transcriptText.trim() || history[0]?.text || SEED_TRANSCRIPTS[0];
  const stagedOutcomeLabel =
    EMULATOR_TRANSCRIPT_OUTCOME_OPTIONS.find((option) => option.value === transcriptOutcome)?.label ??
    transcriptOutcome;
  const deliveryPreview =
    transcriptOutcome === "ready"
      ? `Will deliver: ${transcriptSnippet(stagedTranscript)}`
      : `Will deliver: ${stagedOutcomeLabel} — outcome only, no transcript text.`;
  const scheduledLandsAt = lastDelivered?.landsAt ?? null;
  const countdownRemaining = scheduledLandsAt == null ? null : Math.max(0, Math.ceil((scheduledLandsAt - now) / 1000));
  // The delivered-state keys off snapshot existence alone, not a match
  // against the staged config: staged text/outcome are dock-global
  // iteration state shared across calls, while snapshots are per call SID.
  // Matching would resurrect the form whenever another call restaged.
  const deliveredMatch = lastDelivered !== null;
  const deliveredOutcomeLabel =
    EMULATOR_TRANSCRIPT_OUTCOME_OPTIONS.find((option) => option.value === lastDelivered?.outcome)?.label ??
    lastDelivered?.outcome ??
    "";
  // Prefer the loaded call's own person: lastPgaId can belong to a different
  // agent selection when an old call is loaded into the dock.
  const viewInteractionPgaId = call?.person?.pgaId ?? lastPgaId ?? null;
  const finalizedInteractionId = isEnded ? call?.interactionId : null;

  const phase: DockPhase = !activeCallSid
    ? "idle"
    : !call
      ? "loading"
      : !isEnded
        ? "live"
        : finalizedInteractionId
          ? "done"
          : "wrapup";
  // The call card keeps an actions row only while it has actions (Reset lives
  // in the header now, so wrapup has none).
  const hasCallActions = phase === "live" || (phase === "done" && !!finalizedInteractionId && !!viewInteractionPgaId);

  const handleSampleCaller = () => {
    startBusy("sample");
    setError(null);
    void (async () => {
      try {
        const { caller, hint } = await fetchSampleCaller(requestOptions);
        if (!caller) {
          setError(hint ?? "No person phone numbers found. Enter a phone number manually.");
          logDebug("Sample caller found nobody to call");
          return;
        }
        setPhone(caller.phone);
        logDebug(`Sampled caller ${caller.phone}`);
      } catch (err) {
        setError(describeError(err));
        logDebug(`Sample caller failed: ${describeError(err)}`);
      } finally {
        endBusy("sample");
      }
    })();
  };

  const handleStart = () => {
    // Blank is not a shortcut: number mode needs an explicit number (or a
    // sampled one), and withheld caller ID is an explicit choice.
    if (callerIdKind === "number" && !phone.trim()) {
      setError("Enter a caller phone number, sample one, or choose Anonymous or Unknown.");
      return;
    }
    startBusy("start");
    setError(null);
    void (async () => {
      try {
        const dialPhone = callerIdKind === "number" ? phone.trim() : callerIdKind;
        const started = await startEmulatorCall({ phone: dialPhone, direction }, requestOptions);
        setCall(started);
        setLastCallSid(started.callSid);
        logDebug(`Started ${formatCallSid(started.callSid)} (${directionLabelFor(started.direction)})`);
        navigate(incomingCallPathFor(pathname, started.participantPhone ?? dialPhone, started.callSid) as Path);
      } catch (err) {
        setError(describeError(err));
        logDebug(`Start call failed: ${describeError(err)}`);
      } finally {
        endBusy("start");
      }
    })();
  };

  // Abandons the dock's working call (agent selection included) so another
  // call can be started or opened. The backend rows are untouched.
  const handleResetEmulator = () => {
    if (activeCallSid) {
      logDebug(`Cleared ${formatCallSid(activeCallSid)} from the dock`);
    }
    if (selection?.callSid) {
      clearActiveCallSelectionForCallSid(selection.callSid);
    }
    setLastDelivered(null);
    if (activeCallSid) clearDeliveredSnapshot(activeCallSid);
    setLastCallSid(null);
    setLastPgaId(null);
  };

  const handleEndCall = () => {
    if (!activeCallSid) return;
    const callSid = activeCallSid;
    startBusy("end");
    setError(null);
    void (async () => {
      try {
        const ended = await completeEmulatorCall(callSid, undefined, requestOptions);
        setCall(ended);
        logDebug(`Ended ${formatCallSid(callSid)}`);

        // A real hangup reaches the agent UI instantly (the Flex handoff),
        // separately from the backend webhook. Flip the panel in place — but
        // never resurrect an already-finalized wrap-up.
        if (!ended.interactionId) {
          markActiveCallEndedForCallSid(callSid);
        }

        if (settings.autoDeliver) {
          const typed = transcriptText.trim();
          if (typed) setHistory(pushTranscriptHistory(typed));
          const text = typed || history[0]?.text || SEED_TRANSCRIPTS[0];
          const result = await deliverEmulatorTranscript(
            callSid,
            {
              outcome: transcriptOutcome,
              ...(transcriptOutcome === "ready" ? { transcriptText: text } : {}),
              ...(settings.delaySeconds > 0 ? { delaySeconds: settings.delaySeconds } : {})
            },
            requestOptions
          );
          setCall(result.call);
          logDebug(
            result.scheduled
              ? `Transcript scheduled in ${result.scheduledInSeconds}s (${transcriptOutcome})`
              : `Auto-delivered transcript (${transcriptOutcome})`
          );
          const snapshot = {
            outcome: transcriptOutcome,
            text: transcriptOutcome === "ready" ? text : null,
            landsAt: result.scheduled ? Date.now() + result.scheduledInSeconds * 1000 : null
          };
          setLastDelivered(snapshot);
          saveDeliveredSnapshot(callSid, snapshot);
        } else {
          logDebug("Auto-delivery off — transcript not sent");
        }
        await loadRecentCalls();
      } catch (err) {
        setError(describeError(err));
        logDebug(`End call failed: ${describeError(err)}`);
      } finally {
        endBusy("end");
      }
    })();
  };

  const handleDeliverTranscript = () => {
    if (!activeCallSid) return;
    const callSid = activeCallSid;
    const typed = transcriptText.trim();
    if (typed) setHistory(pushTranscriptHistory(typed));
    const text = typed || history[0]?.text || SEED_TRANSCRIPTS[0];
    const delay = Math.max(0, Number.parseInt(delaySeconds.trim() || "0", 10) || 0);
    updateSettings({ delaySeconds: delay });
    startBusy("transcript");
    setError(null);
    void (async () => {
      try {
        const result = await deliverEmulatorTranscript(
          callSid,
          {
            outcome: transcriptOutcome,
            ...(transcriptOutcome === "ready" ? { transcriptText: text } : {}),
            ...(delay > 0 ? { delaySeconds: delay } : {})
          },
          requestOptions
        );
        setCall(result.call);
        logDebug(
          result.scheduled
            ? `Transcript scheduled in ${result.scheduledInSeconds}s (${transcriptOutcome})`
            : `Delivered transcript (${transcriptOutcome})`
        );
        setTranscriptAccordionOpen(false);
        const snapshot = {
          outcome: transcriptOutcome,
          text: transcriptOutcome === "ready" ? text : null,
          landsAt: result.scheduled ? Date.now() + result.scheduledInSeconds * 1000 : null
        };
        setLastDelivered(snapshot);
        saveDeliveredSnapshot(callSid, snapshot);
      } catch (err) {
        setError(describeError(err));
        logDebug(`Deliver transcript failed: ${describeError(err)}`);
      } finally {
        endBusy("transcript");
      }
    })();
  };

  const handleRemoveHistoryEntry = (id: string) => {
    setHistory(removeTranscriptHistoryEntry(id));
  };

  const handleRestoreHistory = () => {
    setHistory(restoreSeedTranscripts());
  };

  // Shared delivery controls — only one delivery surface renders at a time
  // (on-hangup staging, post-hangup delivery, or late delivery), so a single
  // element per control stays mounted in exactly one place.
  const outcomeField = (
    <Select
      label="Outcome"
      options={EMULATOR_TRANSCRIPT_OUTCOME_OPTIONS}
      value={transcriptOutcome}
      onSelect={(value) => {
        setTranscriptOutcome(value);
        setLastDelivered(null);
        saveStagedTranscript({ text: transcriptText, outcome: value });
      }}
    />
  );

  const delayField = (
    <TextField
      label="Delivery delay (seconds)"
      placeholder="0"
      value={delaySeconds}
      onChangeText={(value) => {
        setDelaySeconds(value);
        updateSettings({ delaySeconds: Math.max(0, Number.parseInt(value.trim() || "0", 10) || 0) });
      }}
      supportingText="Simulates a late-arriving transcript — the summary job retries until it lands."
      variant="outlined"
      className="w-full"
    />
  );

  // Staged transcript, one row: Change expands the picker accordion inline
  // (default collapsed). Only one delivery surface renders at a time, so a
  // single element per control stays mounted in exactly one place.
  const transcriptRow = (
    <div className="flex items-start justify-between gap-2 rounded-lg border border-sys-brand-outline-variant p-2">
      <div className="min-w-0">
        <p className="text-title-small text-sys-brand-on-surface">{transcriptTitleForText(stagedTranscript)}</p>
        <p className="mt-1 line-clamp-2 text-body-small text-sys-brand-on-surface-variant">
          {transcriptSnippet(stagedTranscript)}
        </p>
      </div>
      <Button
        variant="text"
        ariaLabel="Change transcript"
        onClick={() => setTranscriptAccordionOpen((open) => !open)}
        disabled={busyAny}
      >
        Change
      </Button>
    </div>
  );

  const transcriptPicker = (
    <TranscriptPicker
      history={history}
      selectedText={stagedTranscript}
      onSelect={selectTranscript}
      onRemove={handleRemoveHistoryEntry}
      onRestore={handleRestoreHistory}
      disabled={busyAny}
    />
  );

  const deliveredBox = (
    <div className="flex flex-col gap-2 rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container p-2">
      {scheduledLandsAt != null && countdownRemaining !== null ? (
        <p className="flex items-center gap-1 text-body-medium text-sys-brand-on-surface">
          <Icon name="schedule" size={16} />
          {`Scheduled — lands in ${countdownRemaining}s`}
        </p>
      ) : (
        <p className="flex items-center gap-1 text-body-medium text-sys-brand-on-surface">
          <Icon name="check" size={16} />
          {`Delivered — ${deliveredOutcomeLabel}`}
        </p>
      )}
    </div>
  );

  return createPortal(
    <div
      className={`fixed bottom-6 left-6 ${DOCK_Z_INDEX_CLASS} w-[340px] overflow-hidden rounded-xl border border-sys-brand-outline bg-sys-brand-surface-container-low shadow-web-3`}
      data-testid="flex-emulator-dock"
    >
      <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: TWILIO_RED }}>
        <p className="flex items-center gap-2 text-title-small font-bold text-white">
          <img src={`${import.meta.env.BASE_URL}flex-emulator-mark-white.png`} alt="" width={20} height={20} />
          Flex emulator
        </p>
        <div className="flex items-center gap-1">
          {hasActiveCall && (
            <Button
              variant="text"
              icon="restart_alt"
              ariaLabel="Reset emulator"
              onClick={handleResetEmulator}
              disabled={busyAny}
              style={HEADER_BUTTON_STYLE}
            >
              Reset
            </Button>
          )}
          <IconButton
            name="close"
            ariaLabel="End emulator session"
            onClick={() => setFlexEmulatorSessionActive(false)}
            iconClassName="text-white"
          />
        </div>
      </div>

      <PhaseSteps phase={phase} />

      <div className="max-h-[75vh] overflow-y-auto">
        <div className="flex flex-col gap-6 p-4">
          {error && <Banner variant="error" description={error} closable onClose={() => setError(null)} />}

          {phase === "idle" && (
            <section>
              <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">1 • Start a call</p>
              <div className="flex flex-col gap-6">
                <Select
                  label="Caller ID"
                  options={CALLER_ID_OPTIONS}
                  value={callerIdKind}
                  onSelect={(value) => setCallerIdKind(value as CallerIdKind)}
                  disabled={busyAny}
                />
                {callerIdKind === "number" ? (
                  <div className="flex items-start gap-2">
                    <TextField
                      label="Caller phone"
                      placeholder="+14155551212"
                      value={phone}
                      onChangeText={setPhone}
                      supportingText="Any valid number, domestic or international."
                      variant="outlined"
                      className="min-w-0 flex-1"
                    />
                    <div className="mt-2 shrink-0">
                      <Button
                        variant="outlined"
                        icon="casino"
                        ariaLabel="Sample caller"
                        onClick={handleSampleCaller}
                        loading={busyKeys.has("sample")}
                        disabled={busyAny}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="-mt-4 text-body-small text-sys-brand-on-surface-variant">
                    Twilio reports this call&rsquo;s From as &ldquo;{callerIdKind}&rdquo; — no member is matched.
                  </p>
                )}
                <div>
                  <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">Direction</p>
                  <SegmentedButton
                    options={DIRECTION_OPTIONS}
                    value={direction}
                    onChange={(value) => {
                      if (value) setDirection(value as EmulatorDirection);
                    }}
                    fullWidth
                    ariaLabel="Call direction"
                  />
                  <p className="mt-2 text-body-small text-sys-brand-on-surface-variant">
                    {direction === "inbound" ? "A member is calling in." : "You are calling a member."}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="filled"
                    icon="call"
                    ariaLabel="Start call"
                    onClick={handleStart}
                    loading={busyKeys.has("start")}
                    disabled={busyAny}
                  >
                    Start call
                  </Button>
                </div>
              </div>
            </section>
          )}

          {phase === "loading" && (
            <section>
              {busyKeys.has("load") ? (
                <div className="flex items-center gap-3">
                  <Progress size={20} />
                  <p className="text-body-medium text-sys-brand-on-surface-variant">Loading call…</p>
                </div>
              ) : (
                <p className="text-body-medium text-sys-brand-on-surface-variant">Call unavailable.</p>
              )}
            </section>
          )}

          {(phase === "live" || phase === "wrapup" || phase === "done") && call && (
            <section>
              <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">
                {phase === "live" ? "2 • On the call" : phase === "wrapup" ? "2 • Call ended" : "Done"}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-title-small text-sys-brand-on-surface">{callerLabelFor(call.participantPhone)}</p>
                {phase === "live" ? (
                  <Tag tagType="success" tagStyle="standard" size="small">
                    Live
                  </Tag>
                ) : phase === "wrapup" ? (
                  <Tag tagType="surface" tagStyle="standard" size="small">
                    Ended
                  </Tag>
                ) : (
                  <Tag tagType="information" tagStyle="standard" size="small">
                    Finalized
                  </Tag>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-small text-sys-brand-on-surface-variant">
                <span>{directionLabelFor(call.direction)}</span>
                {call.durationSeconds !== null && call.durationSeconds !== undefined && (
                  <>
                    <span>·</span>
                    <span>{call.durationSeconds}s</span>
                  </>
                )}
              </div>
              {hasCallActions && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {phase === "live" && (
                    <Button
                      variant="filled"
                      theme="error"
                      icon="call_end"
                      ariaLabel="End call"
                      onClick={handleEndCall}
                      loading={busyKeys.has("end")}
                      disabled={busyAny}
                    >
                      End call
                    </Button>
                  )}
                  {phase === "live" && activeCallSid !== selection?.callSid && (
                    <Button
                      variant="text"
                      onClick={() =>
                        navigate(incomingCallPathFor(pathname, call.participantPhone, call.callSid) as Path)
                      }
                      disabled={busyAny || !call.participantPhone}
                    >
                      Ring agent UI
                    </Button>
                  )}
                  {phase === "done" && finalizedInteractionId && viewInteractionPgaId && (
                    <Button
                      variant="filled"
                      onClick={() => navigate(interactionPathFor(viewInteractionPgaId, finalizedInteractionId))}
                      disabled={busyAny}
                    >
                      View interaction
                    </Button>
                  )}
                </div>
              )}
            </section>
          )}

          {phase === "live" && (
            <section>
              <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">On hangup</p>
              <div className="flex flex-col gap-3">
                <Switch
                  headline="Auto-deliver transcript"
                  supportingText="Delivers when you end the call."
                  selected={settings.autoDeliver}
                  onChange={() => updateSettings({ autoDeliver: !settings.autoDeliver })}
                />
                {settings.autoDeliver ? (
                  hangupPickerOpen ? (
                    <div className="flex flex-col gap-6">
                      {outcomeField}
                      {transcriptOutcome === "ready" ? (
                        transcriptPicker
                      ) : (
                        <p className="text-body-small text-sys-brand-on-surface-variant">
                          Outcome only — no transcript text.
                        </p>
                      )}
                      {delayField}
                      <p className="-mt-4 text-body-small text-sys-brand-on-surface-variant">{deliveryPreview}</p>
                      <div>
                        <Button variant="text" onClick={() => setHangupPickerOpen(false)}>
                          Done
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <p className="text-body-small text-sys-brand-on-surface-variant">{deliveryPreview}</p>
                      <div>
                        <Button variant="text" onClick={() => setHangupPickerOpen(true)}>
                          Change transcript
                        </Button>
                      </div>
                    </div>
                  )
                ) : (
                  <p className="text-body-small text-sys-brand-on-surface-variant">
                    You&rsquo;ll choose a transcript after hangup.
                  </p>
                )}
              </div>
            </section>
          )}

          {phase === "wrapup" && (
            <section>
              <p className="mb-2 text-label-medium text-sys-brand-on-surface-variant">3 • Transcript</p>
              {deliveredMatch ? (
                deliveredBox
              ) : (
                <div className="flex flex-col gap-6">
                  {outcomeField}
                  {transcriptOutcome === "ready" ? (
                    <>
                      {transcriptRow}
                      {transcriptAccordionOpen && transcriptPicker}
                    </>
                  ) : (
                    <p className="text-body-small text-sys-brand-on-surface-variant">
                      Outcome only — no transcript text.
                    </p>
                  )}
                  {delayField}
                  <div>
                    <Button
                      variant="filled"
                      onClick={handleDeliverTranscript}
                      loading={busyKeys.has("transcript")}
                      disabled={busyAny}
                    >
                      Deliver
                    </Button>
                  </div>
                  {stagedTranscript && (
                    <p className="-mt-4 text-body-small text-sys-brand-on-surface-variant">{deliveryPreview}</p>
                  )}
                </div>
              )}
            </section>
          )}

          {phase === "done" && (
            <section>
              {deliveredMatch || lateTranscriptOpen ? (
                <div className="flex flex-col gap-6">
                  <p className="text-label-medium text-sys-brand-on-surface-variant">Late transcript</p>
                  {deliveredMatch ? (
                    deliveredBox
                  ) : (
                    <>
                      {outcomeField}
                      {transcriptOutcome === "ready" ? (
                        <>
                          {transcriptRow}
                          {transcriptAccordionOpen && transcriptPicker}
                        </>
                      ) : (
                        <p className="text-body-small text-sys-brand-on-surface-variant">
                          Outcome only — no transcript text.
                        </p>
                      )}
                      {delayField}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="filled"
                          onClick={handleDeliverTranscript}
                          loading={busyKeys.has("transcript")}
                          disabled={busyAny}
                        >
                          Deliver
                        </Button>
                        <Button variant="text" onClick={() => setLateTranscriptOpen(false)}>
                          Done
                        </Button>
                      </div>
                      {stagedTranscript && (
                        <p className="-mt-4 text-body-small text-sys-brand-on-surface-variant">{deliveryPreview}</p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <Button variant="text" onClick={() => setLateTranscriptOpen(true)}>
                  Deliver late transcript
                </Button>
              )}
            </section>
          )}

          {phase === "idle" && (
            <section>
              <Button
                variant="text"
                icon={libraryOpen ? "expand_less" : "expand_more"}
                trailingIcon
                ariaLabel={`Transcript library (${history.length})`}
                ariaExpanded={libraryOpen}
                onClick={() => setLibraryOpen((open) => !open)}
              >
                {`Transcript library (${history.length})`}
              </Button>
              {libraryOpen && (
                <div className="mt-2 flex flex-col gap-3">
                  {transcriptPicker}
                  <p className="text-body-small text-sys-brand-on-surface-variant">
                    Staged for the next call&rsquo;s auto-delivery.
                  </p>
                </div>
              )}
            </section>
          )}

          <section>
            <div className="mb-2 flex items-center justify-between">
              <Button
                variant="text"
                icon={recentOpen ? "expand_less" : "expand_more"}
                trailingIcon
                ariaLabel={`Recent calls (${recentCalls.length})`}
                ariaExpanded={recentOpen}
                onClick={() => setRecentOpen((open) => !open)}
              >
                {`Recent calls (${recentCalls.length})`}
              </Button>
              <Button
                variant="text"
                ariaLabel="Refresh recent calls"
                onClick={() => void loadRecentCalls()}
                loading={busyKeys.has("recent")}
              >
                Refresh
              </Button>
            </div>
            {recentOpen && (
              <>
                <p className="mb-2 text-body-small text-sys-brand-on-surface-variant">
                  The dock works one call at a time. Open puts a call in the dock; Reset releases the active one.
                </p>
                {recentCalls.length === 0 ? (
                  <p className="text-body-medium text-sys-brand-on-surface-variant">
                    {busyKeys.has("recent") ? "Loading…" : "No emulated calls yet. Start one above."}
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {recentCalls.map((recent) => {
                      const isActive = recent.callSid === activeCallSid;
                      return (
                        <div
                          key={recent.id}
                          className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2 ${
                            isActive ? "border-sys-brand-primary" : "border-sys-brand-outline"
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="flex flex-wrap items-center gap-2 text-title-small text-sys-brand-on-surface">
                              {callerLabelFor(recent.participantPhone)}
                              <RecentStatusTag call={recent} />
                              {isActive && (
                                <Tag tagType="secondary" tagStyle="standard" size="small">
                                  Active
                                </Tag>
                              )}
                            </p>
                            <p className="mt-1 text-body-small text-sys-brand-on-surface-variant">
                              {directionLabelFor(recent.direction)}
                              {recent.durationSeconds !== null && recent.durationSeconds !== undefined && (
                                <> · {recent.durationSeconds}s</>
                              )}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {!isActive && (
                              <Button
                                variant="text"
                                onClick={() => {
                                  setLastCallSid(recent.callSid);
                                  logDebug(`Opened ${formatCallSid(recent.callSid)} in the dock`);
                                }}
                                disabled={busyAny || !!selection?.callSid}
                              >
                                Open
                              </Button>
                            )}
                            {recent.interactionId && recent.person?.pgaId && (
                              <Button
                                variant="text"
                                disabled={busyAny}
                                onClick={() => {
                                  const personId = recent.person?.pgaId;
                                  const targetInteractionId = recent.interactionId;
                                  if (personId && targetInteractionId) {
                                    navigate(interactionPathFor(personId, targetInteractionId));
                                  }
                                }}
                              >
                                View
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <Button
                variant="text"
                icon={debugOpen ? "expand_less" : "expand_more"}
                trailingIcon
                ariaLabel={`Debug (${debugLog.length})`}
                ariaExpanded={debugOpen}
                onClick={() => setDebugOpen((open) => !open)}
              >
                {`Debug (${debugLog.length})`}
              </Button>
              {debugLog.length > 0 && (
                <Button variant="text" ariaLabel="Clear debug log" onClick={() => setDebugLog([])}>
                  Clear
                </Button>
              )}
            </div>
            {debugOpen &&
              (debugLog.length === 0 ? (
                <p className="text-body-medium text-sys-brand-on-surface-variant">
                  Nothing yet — actions you take in the dock are recorded here.
                </p>
              ) : (
                <div className="rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container p-2">
                  {debugLog.slice(-8).map((entry, index) => (
                    <p key={`${entry.time}-${index}`} className="text-body-small text-sys-brand-on-surface-variant">
                      <span className="text-sys-brand-on-surface">{entry.time}</span> — {entry.message}
                    </p>
                  ))}
                </div>
              ))}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
